import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibInfoOutlineIcon } from '@chewy/kib-icons-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'
import { KibTooltip, KibTooltipTrigger } from '@chewy/kib-tooltip-react'

import NumberField from '~/components/common/NumberField'
import { usePaymentTerminals } from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/hooks/usePaymentTerminals'
import { PaymentMethod } from '~/components/dashboard/invoices/payment/constants/addPaymentDialogV2Constants'
import {
  getCurrentBusiness,
  getCurrentBusinessIsOmniChannel,
  getCurrentUser,
} from '~/store/reducers/auth'
import { triggerErrorDoggo } from '~/utils/errorDoggoUtils'

import { usePaymentMethodValidation } from '../../hooks/usePaymentMethodValidation'
import { usePaymentDialog } from '../context/PaymentDialogProvider'
import { extractBackendErrorMessage } from '../utils/paymentDialogUtils'
import { usePaymentMethodOptions } from '../utils/paymentMethodTranslations'
import CareCreditsLink from './CareCreditsLink'
import ChargeablePaymentFields from './ChargeablePaymentFields'
import PaymentDropdown from './PaymentDropdown'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import styles from './PaymentMethodSection.module.scss'

interface PaymentMethodSectionProps {
  remainingBalance: number | undefined
  setIsFormValid: (isFormValid: boolean) => void
  setSendToTerminal: (sendToTerminal: boolean) => void
}

interface PaymentMethodConfig {
  [PaymentMethod.CREDIT_DEBIT]: ReactNode
  [PaymentMethod.CARECREDIT]: ReactNode
  [PaymentMethod.CASH]: ReactNode
  [PaymentMethod.CHECK]: ReactNode
}

interface PaymentFormData {
  amountReceived: string
  amountToCharge: string
  authNumber: string
  checkAmount: string
  checkNumber: string
  terminal: string
}

const PaymentMethodSection = ({
  remainingBalance,
  setIsFormValid,
  setSendToTerminal,
}: PaymentMethodSectionProps) => {
  const { t } = useTranslation(['Common', 'Dialogs', 'Invoices'])
  const {
    paymentTerminalOptions,
    loading,
    error: terminalError,
    refetch,
  } = usePaymentTerminals()
  const availablePaymentTerminals =
    paymentTerminalOptions.filter((terminal) => terminal.selectable).length > 0
  const { actions, dialogState, invoice, isButtonActionLoading } =
    usePaymentDialog()
  const reduxDispatch = useDispatch()
  const currentUser = useSelector(getCurrentUser)
  const currentBusiness = useSelector(getCurrentBusiness)
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(
    PaymentMethod.CREDIT_DEBIT,
  )
  const [formData, setFormData] = useState<PaymentFormData>({
    amountToCharge: (remainingBalance || 0).toString(),
    checkAmount: (remainingBalance || 0).toString(),
    checkNumber: '',
    authNumber: '',
    amountReceived: '',
    terminal: '',
  })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isDisabled =
    isButtonActionLoading ||
    remainingBalance === 0 ||
    remainingBalance === undefined
  const allPaymentMethods = usePaymentMethodOptions()
  const isOmnichannel = useSelector(getCurrentBusinessIsOmniChannel)

  // Filter out CHECK option for Omnichannel clinics
  const paymentMethods = isOmnichannel
    ? allPaymentMethods.filter((method) => method.value !== PaymentMethod.CHECK)
    : allPaymentMethods

  const { getValidationMessage, validateForm, getNumericValue } =
    usePaymentMethodValidation({
      selectedMethod,
      formData,
      remainingBalance,
    })

  const handleFormDataChange = (
    field: keyof PaymentFormData,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const resetFormData = () => {
    setSelectedMethod(PaymentMethod.CREDIT_DEBIT)
    setFormData({
      amountToCharge: (remainingBalance || 0).toString(),
      checkAmount: (remainingBalance || 0).toString(),
      checkNumber: '',
      authNumber: '',
      amountReceived: '',
      terminal: '',
    })
  }

  const calculateChangeDue = () => {
    const received = getNumericValue('amountReceived')
    return Math.max(0, received - (remainingBalance || 0))
  }

  useEffect(() => {
    refetch()
  }, [])

  useEffect(() => {
    if (terminalError && !loading) {
      const errorMessage = t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.NO_TERMINALS_AVAILABLE',
      )
      const backendErrorMessage = extractBackendErrorMessage(
        terminalError,
        errorMessage,
      )
      triggerErrorDoggo(
        reduxDispatch,
        backendErrorMessage,
        500,
        currentUser,
        currentBusiness,
      )
    }
  }, [terminalError, loading, reduxDispatch, t, currentUser, currentBusiness])

  // Reset formData when a partial payment is detected.
  // We need both dependencies: when dueToPay changes OR when hasPartialPayment becomes true.
  // The condition inside ensures we only reset when hasPartialPayment is true.
  useEffect(() => {
    if (dialogState.hasPartialPayment) {
      resetFormData()
    }
  }, [invoice.dueToPay, dialogState.hasPartialPayment])

  useEffect(() => {
    if (remainingBalance !== undefined) {
      setFormData((prev) => ({
        ...prev,
        amountToCharge: remainingBalance.toString(),
        checkAmount: remainingBalance.toString(),
        amountReceived: remainingBalance === 0 ? '' : prev.amountReceived,
      }))
    }
  }, [remainingBalance])

  useEffect(() => {
    const isValid = validateForm()
    setIsFormValid(isValid)
    setSendToTerminal(selectedMethod === PaymentMethod.CREDIT_DEBIT)
  }, [
    formData,
    selectedMethod,
    setIsFormValid,
    remainingBalance,
    setSendToTerminal,
    validateForm,
  ])

  useEffect(() => {
    const paymentMethodMap: Record<
      PaymentMethod,
      () => {
        amountToCharge: string
        paymentTerminal: string
        paymentTerminalDisplayName: string
      }
    > = {
      [PaymentMethod.CREDIT_DEBIT]: () => {
        const selectedTerminal = paymentTerminalOptions.find(
          (terminal) => terminal.value === formData.terminal,
        )
        return {
          amountToCharge: formData.amountToCharge,
          paymentTerminal: selectedTerminal?.value || '',
          paymentTerminalDisplayName: selectedTerminal?.label || '',
        }
      },
      [PaymentMethod.CARECREDIT]: () => ({
        amountToCharge: formData.amountToCharge,
        paymentTerminal: '',
        paymentTerminalDisplayName: '',
      }),
      [PaymentMethod.CASH]: () => ({
        amountToCharge: Math.min(
          getNumericValue('amountReceived'),
          remainingBalance || 0,
        ).toString(),
        paymentTerminal: '',
        paymentTerminalDisplayName: '',
      }),
      [PaymentMethod.CHECK]: () => ({
        amountToCharge: Math.min(
          getNumericValue('checkAmount'),
          remainingBalance || 0,
        ).toString(),
        paymentTerminal: '',
        paymentTerminalDisplayName: '',
      }),
    }

    const { amountToCharge, paymentTerminal, paymentTerminalDisplayName } =
      paymentMethodMap[selectedMethod]()
    const paymentDataValidationMap: Record<PaymentMethod, () => boolean> = {
      [PaymentMethod.CREDIT_DEBIT]: () => getNumericValue('amountToCharge') > 0,
      [PaymentMethod.CARECREDIT]: () => getNumericValue('amountToCharge') > 0,
      [PaymentMethod.CASH]: () => getNumericValue('amountReceived') > 0,
      [PaymentMethod.CHECK]: () => getNumericValue('checkAmount') > 0,
    } as const

    const shouldSetPaymentData = paymentDataValidationMap[selectedMethod]?.()
    const isRemainingBalanceZero = remainingBalance === 0

    // Update paymentData when:
    // 1. Validation passes (shouldSetPaymentData is true), OR
    // 2. remainingBalance is 0 (to sync amountToCharge to "0" when account credits cover full balance)
    if (shouldSetPaymentData || isRemainingBalanceZero) {
      const basePaymentData = {
        amountToCharge,
        paymentMethod: selectedMethod as string,
        paymentTerminal,
        paymentTerminalDisplayName,
        authNumber: '',
        checkNumber: '',
        amountReceived: '',
        changeDue: '',
        braintreeTransactionId: '',
        rhapsodyTransactionId: '',
        paidBy: '',
      }

      if (selectedMethod === PaymentMethod.CARECREDIT) {
        actions.setPaymentData({
          ...basePaymentData,
          authNumber: formData.authNumber,
        })
      } else if (selectedMethod === PaymentMethod.CHECK) {
        actions.setPaymentData({
          ...basePaymentData,
          checkNumber: formData.checkNumber,
        })
      } else if (selectedMethod === PaymentMethod.CASH) {
        actions.setPaymentData({
          ...basePaymentData,
          amountReceived: formData.amountReceived,
          changeDue: calculateChangeDue().toString(),
        })
      } else {
        actions.setPaymentData({ ...basePaymentData })
      }
    }
  }, [
    selectedMethod,
    formData,
    remainingBalance,
    actions,
    getNumericValue,
    paymentTerminalOptions,
  ])

  const paymentMethodConfig: PaymentMethodConfig = {
    [PaymentMethod.CREDIT_DEBIT]: (
      <div className={styles.fieldGrid}>
        <div className={styles.fieldColumn}>
          <ChargeablePaymentFields
            amountToCharge={formData.amountToCharge}
            isDisabled={isDisabled}
            key={`${selectedMethod}-amountToCharge`}
            paymentMethods={paymentMethods}
            selectedMethod={selectedMethod}
            validationMessage={getValidationMessage(
              'amountToCharge',
              getNumericValue('amountToCharge'),
            )}
            onAmountChange={(value) =>
              handleFormDataChange('amountToCharge', value)
            }
            onMethodChange={(value) =>
              setSelectedMethod(value as PaymentMethod)
            }
          />
        </div>
        <div className={styles.fieldColumn}>
          <PaymentDropdown
            required
            disabled={isDisabled || loading || !availablePaymentTerminals}
            label={t('Dialogs:ADD_PAYMENT_DIALOG_V2.PAYMENT_TERMINAL')}
            messages={{
              valueMissing: t(
                'Dialogs:ADD_PAYMENT_DIALOG_V2.VALIDATION.PAYMENT_TERMINAL_REQUIRED',
              ),
            }}
            options={paymentTerminalOptions}
            placeholder={
              loading ? t('Common:LOADING') : t('Common:SELECT_ACTION')
            }
            value={formData.terminal || ''}
            onChange={(value) => handleFormDataChange('terminal', value)}
          />
        </div>
      </div>
    ),
    [PaymentMethod.CARECREDIT]: (
      <div className={styles.fieldGrid}>
        <div className={styles.fieldColumn}>
          <ChargeablePaymentFields
            amountToCharge={formData.amountToCharge}
            isDisabled={isDisabled}
            key={`${selectedMethod}-amountToCharge`}
            paymentMethods={paymentMethods}
            selectedMethod={selectedMethod}
            validationMessage={getValidationMessage(
              'amountToCharge',
              getNumericValue('amountToCharge'),
            )}
            onAmountChange={(value) =>
              handleFormDataChange('amountToCharge', value)
            }
            onMethodChange={(value) =>
              setSelectedMethod(value as PaymentMethod)
            }
          />
          <div className={styles.fieldContainerWithTooltip}>
            <NumberField
              required
              disabled={isDisabled}
              isMoney={false}
              key={`${selectedMethod}-authNumber`}
              label={t('Dialogs:ADD_PAYMENT_DIALOG_V2.AUTHORIZATION_NUMBER')}
              validationMessage={getValidationMessage(
                'authNumber',
                getNumericValue('authNumber'),
              )}
              value={formData.authNumber || ''}
              onChange={(value) => handleFormDataChange('authNumber', value)}
            />
            <KibTooltipTrigger ref={buttonRef}>
              <KibInfoOutlineIcon className={styles.infoIcon} />
            </KibTooltipTrigger>
            <KibTooltip
              className={styles.tooltip}
              content={t(
                'Dialogs:ADD_PAYMENT_DIALOG_V2.CARECREDIT_TOOLTIP_TEXT',
              )}
              placement="top"
              popperModifiers={[]}
              trigger={buttonRef}
            />
          </div>
        </div>
        <div className={styles.fieldColumn}>
          <CareCreditsLink />
        </div>
      </div>
    ),
    [PaymentMethod.CASH]: (
      <div className={styles.fieldGrid}>
        <div className={styles.fieldColumn}>
          <PaymentDropdown
            disabled={isDisabled}
            label={t('Common:PAYMENTS.PAYMENT_METHOD')}
            options={paymentMethods}
            value={selectedMethod}
            onChange={(value) => setSelectedMethod(value as PaymentMethod)}
          />
          <NumberField
            isMoney
            required
            disabled={isDisabled}
            key={`${selectedMethod}-amountReceived`}
            label={t('Dialogs:ADD_PAYMENT_DIALOG_V2.AMOUNT_RECEIVED')}
            validationMessage={getValidationMessage(
              'amountReceived',
              getNumericValue('amountReceived'),
            )}
            value={formData.amountReceived || ''}
            onChange={(value) => handleFormDataChange('amountReceived', value)}
          />
          <NumberField
            isMoney
            readOnly
            key={`${selectedMethod}-changeDue`}
            label={t('Common:CHANGE_DUE')}
            required={false}
            value={calculateChangeDue().toFixed(2)}
            onChange={() => {}}
          />
        </div>
      </div>
    ),
    [PaymentMethod.CHECK]: (
      <div className={styles.fieldGrid}>
        <div className={styles.fieldColumn}>
          <PaymentDropdown
            disabled={isDisabled}
            label={t('Common:PAYMENTS.PAYMENT_METHOD')}
            options={paymentMethods}
            value={selectedMethod}
            onChange={(value) => setSelectedMethod(value as PaymentMethod)}
          />
          <NumberField
            isMoney
            required
            disabled={isDisabled}
            key={`${selectedMethod}-checkAmount`}
            label={t('Dialogs:ADD_PAYMENT_DIALOG_V2.CHECK_AMOUNT')}
            validationMessage={getValidationMessage(
              'checkAmount',
              getNumericValue('checkAmount'),
            )}
            value={formData.checkAmount || ''}
            onChange={(value) => handleFormDataChange('checkAmount', value)}
          />
          <NumberField
            required
            disabled={isDisabled}
            isMoney={false}
            key={`${selectedMethod}-checkNumber`}
            label={t('Dialogs:ADD_PAYMENT_DIALOG_V2.CHECK_NUMBER')}
            maxLength={10}
            validationMessage={getValidationMessage(
              'checkNumber',
              getNumericValue('checkNumber'),
            )}
            value={formData.checkNumber || ''}
            onChange={(value) => handleFormDataChange('checkNumber', value)}
          />
        </div>
      </div>
    ),
  } as const

  return (
    <div className={styles.paymentMethodContainer}>
      {selectedMethod === PaymentMethod.CREDIT_DEBIT &&
        !loading &&
        !availablePaymentTerminals && (
          <KibTopLevelAlert
            heading={t('Dialogs:ADD_PAYMENT_DIALOG_V2.NO_TERMINALS_AVAILABLE')}
            severity="error"
          >
            {t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.NO_TERMINALS_AVAILABLE_HELP_TEXT',
            )}
          </KibTopLevelAlert>
        )}
      <h4 className={styles.sectionTitle}>
        {t('Common:PAYMENTS.PAYMENT_INFORMATION')}
      </h4>
      {paymentMethodConfig[selectedMethod]}
    </div>
  )
}

export default PaymentMethodSection
